import { DeliveryRequestStatus, IDeliveryRequestDao } from 'core/api/types/delivery-requests.interface';

/**
 * How a Delivery Request's state is described to a person.
 *
 * Mirrors functions/src/constants/delivery-request-status.ts. The wording is
 * the portal's own: the stored values are identifiers, and "needsReview" is
 * not a sentence.
 */

export const StatusLabels: Record<DeliveryRequestStatus, string> = {
  received: 'Waiting to be handled',
  needsReview: 'Needs review',
  booked: 'Booked in-house',
  forwarded: 'Forwarded on',
  // Historical only: nothing produces this status since the Close decision was
  // removed on 17 Sep 2026. Still labelled "Closed" rather than "Declined"
  // because declining forwards a request on (D3), so the requests already
  // carrying it were closed, not declined.
  declined: 'Closed',
  // Never rendered on the requests screen — a request that could not be parsed
  // is kept for debugging and nothing else. Labelled anyway so the reporting
  // screen, which does still count them, has words for the number.
  unreadable: 'Could not be read',
};

/**
 * The states where nothing happens until a person does something.
 *
 * `received` is deliberately not one: it means the handling is being applied,
 * which is a moment rather than a queue.
 *
 * `unreadable` is deliberately not one either, since 17 Sep 2026. An email that
 * could not be parsed gives a reviewer nothing to act on — there is no request
 * to book and no type to work out where a forwarded one would go — so putting
 * it in front of them only grew a queue nobody could empty. They are still
 * written to `deliveryRequests` and can be read out of Firestore when the
 * parser needs debugging.
 */
export const AWAITING_ACTION: DeliveryRequestStatus[] = ['needsReview'];

export const isAwaitingAction = (request: Pick<IDeliveryRequestDao, 'status'>): boolean =>
  AWAITING_ACTION.includes(request.status);

/** Why a request needs a person, in words rather than codes. */
export const DeliveryRequestExceptionLabels: Record<string, string> = {
  parseFailed: 'Could not read the request',
  unknownRequestType: 'Unrecognised request type',
  collectionUnresolved: 'Collection address could not be found',
  destinationUnresolved: 'Destination address could not be found',
  invalidDateTime: 'Date or time is not valid',
  dateInPast: 'Required time has already passed',
  unexpectedCostCentre: 'Cost centre is not six digits',
  capabilityUnavailable: 'No vehicle available with the capability required',
};
